import React, { useEffect, useRef, useState } from 'react';
import { Redirect, useParams } from 'react-router';
import { DyteService } from '../_service';
import { withSession } from '../_context/session.context';
import { Loader } from './loader.component';
import { createJoinMeetingLink } from './join-meeting.page';
import { Helmet } from 'react-helmet';


import { useRealtimeKitClient } from '@cloudflare/realtimekit-react';
import { RtkMeeting, generateConfig } from '@cloudflare/realtimekit-react-ui';

export const MEETING_PAGE_ROUTE = "/:meetingId";

/**
 * Recursively remove a UI component (by tag name, e.g. "rtk-notifications")
 * from a RealtimeKit UI config tree so it is never mounted. Handles plain
 * string entries and [name, props] tuples inside children arrays. This is the
 * reliable way to suppress notifications — the config booleans alone did not
 * take effect in this RealtimeKit version.
 */
function removeUiComponent(node, name) {
    if (Array.isArray(node)) {
        for (let i = node.length - 1; i >= 0; i--) {
            const item = node[i];
            if (item === name || (Array.isArray(item) && item[0] === name)) {
                node.splice(i, 1);
            } else {
                removeUiComponent(item, name);
            }
        }
    } else if (node && typeof node === 'object') {
        Object.keys(node).forEach((k) => removeUiComponent(node[k], name));
    }
}

export const createMeetingPageRoute = meetingId => `/${meetingId}`;

function MeetingPageComponent({ user, setUser }) {

    // get meeting id
    const { meetingId } = useParams();

    // get token
    const sp = new URLSearchParams(window.location.search);
    const token = sp.get('token');

    // if user name is not update, let the user update it first, if token is there reauth user
    if (!user || !user.name || user.name === "New User" || token) {
        return (
            <Redirect to={createJoinMeetingLink(meetingId) + `?token=${token ? token : ""}`} />
        )
    }

    return (
        <div className="MeetingPage">
            <MeetingComponent
                user={user} />
        </div>
    )

}

const MeetingPage = withSession(MeetingPageComponent);

export default MeetingPage;


function MeetingComponent({ user }) {

    // get 
    const { meetingId } = useParams();

    const [meetingObj, initMeeting] = useRealtimeKitClient();
    const [config, setConfig] = useState(undefined);

    // meeting
    const [isLoading, setIsLoading] = useState(false);
    const [meeting, setMeeting] = useState(null);
    const [message, setMessage] = useState("");

    const [userName, setUserName] = useState("Anonymous User");
    const [newUserName, setNewUserName] = useState("");

    const [meetingLeft, setMeetingLeft] = useState(false);

    const meetingRef = useRef(null);
    /**
     * meeting has the following
     * 
     * authToken
     * clientId
     * endingKey
     * isBeingRecorded
     * meetingId
     * roomName
     */

    useEffect(() => {
        if (meeting?.authToken) {
            initMeeting({
                authToken: meeting?.authToken,
                defaults: {
                    audio: false,
                    video: false,
                },
            })
        }
    }, [meeting]);

    // config init
    useEffect(() => {
        if (!meetingObj || config !== undefined) return;
        const g = generateConfig(meetingObj.self.config, meetingObj);
        const { config: meetingConfig } = g;

        const silenced = {
            participant_joined: false,
            participant_left: false,
            participant_joined_waitlist: false,
            chat: false,
            polls: false,
            webinar: false,
            tab_sync: false,
        };

        // Always silence notification SOUNDS (a large webinar would otherwise
        // ding on every join/message). Keep the rest of the generated config
        // (videoFit etc.) instead of replacing it wholesale.
        meetingConfig.config = {
            ...meetingConfig.config,
            notification_sounds: { ...silenced },
        };

        // Community admin setting (from the meeting-data payload): when on,
        // suppress the visual join/leave & chat toasts for everyone by removing
        // the notifications component from the meeting UI entirely.
        if (meeting?.disableMeetingNotifications) {
            meetingConfig.config.notifications = { ...silenced };
            removeUiComponent(meetingConfig, 'rtk-notifications');
        }

        setConfig(meetingConfig); // set the config in state
    }, [meetingObj, meeting])

    useEffect(() => {
        if (user) {
            setUserName(user.name);
        }
    }, [user]);

    useEffect(() => {
        setIsLoading(true);
        // get meeting only if username is set
        if (userName !== "Anonymous User") {
            // get meeting info
            DyteService.getMeetingData(user, meetingId, userName)
                .then(response => {
                    // update them meeting data
                    setIsLoading(false);
                    setMeeting(response.payload);
                })
                .catch(err => {
                    const m = err && err.response && err.response.data && err.response.data.message;
                    setMessage(m ? m : "Something went wrong!");
                    setIsLoading(false);

                });
        }
    }, [meetingId, user, userName]);

    // enable wake lock
    useEffect(() => {
        if (config) {
            enableScreenWakeLock();
        }
    }, [config]);

    const enableScreenWakeLock = async () => {
        // Create a reference for the Wake Lock.
        let wakeLock = null;

        // create an async function to request a wake lock
        try {
            wakeLock = await navigator.wakeLock.request("screen");
            console.log("Wake Lock is active!");
        } catch (err) {
            // The Wake Lock request has failed - usually system related, such as battery.
            console.log("Wake Lock request failed - ", `${err.name}, ${err.message}`);
        }

        return wakeLock;
    }

    const newNameHasErrors = !newUserName || !newUserName.trim();

    const proceed = () => {
        if (newNameHasErrors) {
            return;
        } else {
            setUserName(newUserName);
        }
    }

    if (!user) {
        // redirect to join meeting
        return (
            <Redirect to={createJoinMeetingLink(meetingId)} />
        )
    }

    if (!meeting || !config || meetingLeft) {
        return (
            <div className="MeetingPage flex flex-col justify-center items-center py-10">
                {
                    isLoading || !config ? (
                        <Loader />
                    ) : <></>
                }
                <span className="message">{meetingLeft ? "Meeting left, please close the window!" : message}</span>
            </div>
        )
    }

    return (
        <>
            {
                meeting.favicon ? (
                    <Helmet>
                        <link rel="icon" href={meeting.favicon} />
                        <title>{meeting.communityName}</title>
                    </Helmet>
                ) : <></>
            }
            {
                config ? (
                    <RtkMeeting
                        meeting={meetingObj}
                        config={config}
                        mode="fill"
                    />
                ) : <></>
            }
        </>
    );
}